import { createEnv } from '@t3-oss/env-nextjs';
import { z } from 'zod';

// Don't add NODE_ENV into T3 Env, it changes the tree-shaking behavior
export const Env = createEnv({
  server: {
    ANALYZE: z.enum(['true', 'false']).optional().transform(value => value === 'true'),
    ANTHROPIC_API_KEY: z.string().default(''),
    AWS_ACCESS_KEY_ID: z.string().default(''),
    AWS_RECORDINGS_BUCKET: z.string().default('coco-recordings-store'),
    AWS_REGION: z.string().default('us-east-1'),
    AWS_SECRET_ACCESS_KEY: z.string().default(''),
    BUGSNAG_SERVER_API_KEY: z.string().default(''),
    DAILY_API_KEY: z.string().default(''),
    DATABASE_URL: z.string(),
    DD_AGENT_HOST: z.string().default(''),
    DISCO_API_BASE_URL: z.string().default(''),
    DISCO_API_KEY: z.string().default(''),
    FILE_UPLOAD_BUCKET: z.string().default(''),
    GCP_PROJECT_ID: z.string().default(''),
    GCP_PROJECT_NUMBER: z.string().default(''),
    GCP_SERVICE_ACCOUNT_EMAIL: z.string().default(''),
    GCP_WORKLOAD_IDENTITY_POOL_ID: z.string().default(''),
    GCP_WORKLOAD_IDENTITY_POOL_PROVIDER_ID: z.string().default(''),
    GOOGLE_CLIENT_EMAIL: z.string().default(''),
    GOOGLE_PRIVATE_KEY: z.string().default(''),
    GRADING_API_URL: z.string().default(''),
    GRADING_BEARER_TOKEN: z.string().default(''),
    INTERVIEW_BEARER_TOKEN: z.string(),
    LANGFUSE_BASE_URL: z.string().default('https://cloud.langfuse.com'),
    LANGFUSE_PUBLIC_KEY: z.string().default(''),
    LANGFUSE_SECRET_KEY: z.string().default(''),
    LAUNCHDARKLY_SDK_KEY: z.string(),
    LINEAR_API_KEY: z.string().default(''),
    MAILGUN_DOMAIN: z.string().default('mail.joinera.ai'),
    MAILGUN_SENDING_KEY: z.string().default(''),
    MUX_TOKEN_ID: z.string().default(''),
    MUX_TOKEN_SECRET: z.string().default(''),
    ONBOARDING_PROJECT_ID: z.string().default('db2c8922-94a9-4d28-b223-b8cc2f56d758'),
    OPENAI_API_KEY: z.string(),
    SLACK_BOT_TOKEN: z.string().default(''),
    SLACK_CHANNEL_ID: z.string().default(''),
    SLACK_PAYMENTS_SUPPORT_CHANNEL_ID: z.string().default(''),
    SLACK_REPORT_ISSUE_CHANNEL_ID: z.string().default(''),
    SLACK_USER_REPORTS_CHANNEL_ID: z.string().default(''),
    SLACK_WEBHOOK_URL: z.string().default(''),
    SUPABASE_SERVICE_ROLE_KEY: z.string().default(''),
    TEMPORAL_ADDRESS: z.string(),
    TEMPORAL_CA_KEY: z.string(),
    TEMPORAL_CA_PEM: z.string(),
    TEMPORAL_NAMESPACE: z.string(),
    TEMPORAL_TASK_QUEUE: z.string(),
    TIPTAP_AI_SECRET: z.string().default(''),
    TIPTAP_PRO_TOKEN: z.string().default(''),
    USE_LOCAL_WEAVIATE: z.enum(['true', 'false']).transform(value => value === 'true'),
    VERCEL_OIDC_TOKEN: z.string().default(''),
    VERTEX_REGION: z.string().default('us-central1'),
    WEAVIATE_API_KEY: z.string(),
    WEAVIATE_LATEST_COLLECTION_NAME: z.string(),
    WEAVIATE_URL: z.string(),
  },
  // Client/shared environment variables must be prefixed with NEXT_PUBLIC_ for them to be accessible on the client side.
  client: {
    NEXT_PUBLIC_BUGSNAG_CLIENT_API_KEY: z.string().default(''),
    NEXT_PUBLIC_DATADOG_APPLICATION_ID: z.string().default(''),
    NEXT_PUBLIC_DATADOG_CLIENT_TOKEN: z.string().default(''),
    NEXT_PUBLIC_DATADOG_ENV: z.string().default(''),
    NEXT_PUBLIC_DATADOG_SERVICE_NAME: z.string().default(''),
    NEXT_PUBLIC_HANDSHAKE_PROVIDER_DOMAIN: z.string().default(''),
    NEXT_PUBLIC_HANDSHAKE_SSO_AUTH_URL: z.string().default(''),
    NEXT_PUBLIC_INTERVIEW_ENGINE_1_URL: z.string().url(),
    NEXT_PUBLIC_INTERVIEW_ENGINE_2_URL: z.string().url(),
    NEXT_PUBLIC_SUPABASE_ANON_KEY: z.string(),
    NEXT_PUBLIC_SUPABASE_URL: z.string().url(),
    NEXT_PUBLIC_TIPTAP_APP_ID: z.string().default(''),
  },
  shared: {
    NEXT_PUBLIC_APP_ENVIRONMENT: z.enum(['development', 'production', 'test', 'staging']).default('development'),
  },
  // For Next.js >= 13.4.4, you only need to destructure client variables
  runtimeEnv: {
    ANALYZE: process.env.ANALYZE,
    ANTHROPIC_API_KEY: process.env.ANTHROPIC_API_KEY,
    AWS_ACCESS_KEY_ID: process.env.AWS_ACCESS_KEY_ID,
    AWS_RECORDINGS_BUCKET: process.env.AWS_RECORDINGS_BUCKET,
    AWS_REGION: process.env.AWS_REGION,
    AWS_SECRET_ACCESS_KEY: process.env.AWS_SECRET_ACCESS_KEY,
    BUGSNAG_SERVER_API_KEY: process.env.BUGSNAG_SERVER_API_KEY,
    DAILY_API_KEY: process.env.DAILY_API_KEY,
    DATABASE_URL: process.env.DATABASE_URL,
    DD_AGENT_HOST: process.env.DD_AGENT_HOST,
    DISCO_API_BASE_URL: process.env.DISCO_API_BASE_URL,
    DISCO_API_KEY: process.env.DISCO_API_KEY,
    FILE_UPLOAD_BUCKET: process.env.FILE_UPLOAD_BUCKET,
    GCP_PROJECT_ID: process.env.GCP_PROJECT_ID,
    GCP_PROJECT_NUMBER: process.env.GCP_PROJECT_NUMBER,
    GCP_SERVICE_ACCOUNT_EMAIL: process.env.GCP_SERVICE_ACCOUNT_EMAIL,
    GCP_WORKLOAD_IDENTITY_POOL_ID: process.env.GCP_WORKLOAD_IDENTITY_POOL_ID,
    GCP_WORKLOAD_IDENTITY_POOL_PROVIDER_ID: process.env.GCP_WORKLOAD_IDENTITY_POOL_PROVIDER_ID,
    GOOGLE_CLIENT_EMAIL: process.env.GOOGLE_CLIENT_EMAIL,
    GOOGLE_PRIVATE_KEY: process.env.GOOGLE_PRIVATE_KEY,
    GRADING_API_URL: process.env.GRADING_API_URL,
    GRADING_BEARER_TOKEN: process.env.GRADING_BEARER_TOKEN,
    INTERVIEW_BEARER_TOKEN: process.env.INTERVIEW_BEARER_TOKEN,
    LANGFUSE_BASE_URL: process.env.LANGFUSE_BASE_URL,
    LANGFUSE_PUBLIC_KEY: process.env.LANGFUSE_PUBLIC_KEY,
    LANGFUSE_SECRET_KEY: process.env.LANGFUSE_SECRET_KEY,
    LAUNCHDARKLY_SDK_KEY: process.env.LAUNCHDARKLY_SDK_KEY,
    LINEAR_API_KEY: process.env.LINEAR_API_KEY,
    MAILGUN_DOMAIN: process.env.MAILGUN_DOMAIN,
    MAILGUN_SENDING_KEY: process.env.MAILGUN_SENDING_KEY,
    MUX_TOKEN_ID: process.env.MUX_TOKEN_ID,
    MUX_TOKEN_SECRET: process.env.MUX_TOKEN_SECRET,
    NEXT_PUBLIC_APP_ENVIRONMENT: process.env.NEXT_PUBLIC_APP_ENVIRONMENT,
    NEXT_PUBLIC_BUGSNAG_CLIENT_API_KEY: process.env.NEXT_PUBLIC_BUGSNAG_CLIENT_API_KEY,
    NEXT_PUBLIC_DATADOG_APPLICATION_ID: process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID,
    NEXT_PUBLIC_DATADOG_CLIENT_TOKEN: process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN,
    NEXT_PUBLIC_DATADOG_ENV: process.env.NEXT_PUBLIC_DATADOG_ENV,
    NEXT_PUBLIC_DATADOG_SERVICE_NAME: process.env.NEXT_PUBLIC_DATADOG_SERVICE_NAME,
    NEXT_PUBLIC_HANDSHAKE_PROVIDER_DOMAIN: process.env.NEXT_PUBLIC_HANDSHAKE_PROVIDER_DOMAIN,
    NEXT_PUBLIC_HANDSHAKE_SSO_AUTH_URL: process.env.NEXT_PUBLIC_HANDSHAKE_SSO_AUTH_URL,
    NEXT_PUBLIC_INTERVIEW_ENGINE_1_URL: process.env.NEXT_PUBLIC_INTERVIEW_ENGINE_1_URL,
    NEXT_PUBLIC_INTERVIEW_ENGINE_2_URL: process.env.NEXT_PUBLIC_INTERVIEW_ENGINE_2_URL,
    NEXT_PUBLIC_SUPABASE_ANON_KEY: process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY,
    NEXT_PUBLIC_SUPABASE_URL: process.env.NEXT_PUBLIC_SUPABASE_URL,
    NEXT_PUBLIC_TIPTAP_APP_ID: process.env.NEXT_PUBLIC_TIPTAP_APP_ID,
    ONBOARDING_PROJECT_ID: process.env.ONBOARDING_PROJECT_ID,
    OPENAI_API_KEY: process.env.OPENAI_API_KEY,
    SLACK_BOT_TOKEN: process.env.SLACK_BOT_TOKEN,
    SLACK_CHANNEL_ID: process.env.SLACK_CHANNEL_ID,
    SLACK_PAYMENTS_SUPPORT_CHANNEL_ID: process.env.SLACK_PAYMENTS_SUPPORT_CHANNEL_ID,
    SLACK_REPORT_ISSUE_CHANNEL_ID: process.env.SLACK_REPORT_ISSUE_CHANNEL_ID,
    SLACK_USER_REPORTS_CHANNEL_ID: process.env.SLACK_USER_REPORTS_CHANNEL_ID,
    SLACK_WEBHOOK_URL: process.env.SLACK_WEBHOOK_URL,
    SUPABASE_SERVICE_ROLE_KEY: process.env.SUPABASE_SERVICE_ROLE_KEY,
    TEMPORAL_ADDRESS: process.env.TEMPORAL_ADDRESS,
    TEMPORAL_CA_KEY: process.env.TEMPORAL_CA_KEY,
    TEMPORAL_CA_PEM: process.env.TEMPORAL_CA_PEM,
    TEMPORAL_NAMESPACE: process.env.TEMPORAL_NAMESPACE,
    TEMPORAL_TASK_QUEUE: process.env.TEMPORAL_TASK_QUEUE,
    TIPTAP_AI_SECRET: process.env.TIPTAP_AI_SECRET,
    TIPTAP_PRO_TOKEN: process.env.TIPTAP_PRO_TOKEN,
    USE_LOCAL_WEAVIATE: process.env.USE_LOCAL_WEAVIATE,
    VERCEL_OIDC_TOKEN: process.env.VERCEL_OIDC_TOKEN,
    VERTEX_REGION: process.env.VERTEX_REGION,
    WEAVIATE_API_KEY: process.env.WEAVIATE_API_KEY,
    WEAVIATE_LATEST_COLLECTION_NAME: process.env.WEAVIATE_LATEST_COLLECTION_NAME,
    WEAVIATE_URL: process.env.WEAVIATE_URL,
  },
});
